import { ApiError } from 'app/core/api/api-core/api-core.types';
import {
  silenceError,
  useMergeApiErrors,
} from 'app/core/errors/error-utils';
import { Obj, Opt } from 'app/tools/general.types';
import { useOnUnmount } from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
  UseFormMethods,
  UseFormOptions,
} from 'react-hook-form';
import { Observable, Subscription } from 'rxjs';
import { Merge } from 'type-fest';

const useAddRequestErrorsToForm = (
  setError: UseFormMethods['setError'],
  getValues: UseFormMethods['getValues'],
  requestError: ApiError = {}
): void => {
  useEffect(() => {
    const fields = _.keys(getValues());
    _(requestError)
      .omit('detail')
      .pick(fields) // TODO: see if this is still necessary after update HookForm
      .forEach((errorFieldValue, errorFieldName) =>
        setError(errorFieldName, {
          type: 'server',
          message: errorFieldValue,
        })
      );
  }, [getValues, requestError, setError]);
};

type ApplyDefaultsOpts = Pick<
  UseFormMethods,
  'getValues' | 'setValue'
> &
  Pick<UseFormOptions, 'defaultValues'>;
export const applyDefaultsIfEmpty = ({
  defaultValues,
  getValues,
  setValue,
}: ApplyDefaultsOpts): void => {
  const formValues = getValues();
  _.forOwn(formValues, (v, key) => {
    // form value is considered empty if it is falsy, or an empty
    // object/array (lodash .isEmpty). The exception is a value of 0
    // which is not considered 'empty'
    const valueEmpty = _.isEmpty(v) && v !== 0;
    if (valueEmpty && defaultValues?.[key]) {
      setValue(key, defaultValues[key]);
    }
  });
};

type CustomUseFormMethods<
  TFieldValues extends FieldValues = FieldValues
> = Merge<
  UseFormMethods<TFieldValues>,
  {
    inputProps: Pick<
      UseFormMethods<TFieldValues>,
      'control' | 'errors'
    > & { defaults: Partial<TFieldValues> };
    handleSubmit: (
      onValid: CustomSubmitHandler<TFieldValues>,
      onInvalid?: SubmitErrorHandler<TFieldValues>
    ) => ReturnType<UseFormMethods<TFieldValues>['handleSubmit']>;
  }
>;

type CustomUseFormOptions<
  TFieldValues extends FieldValues = FieldValues,
  TContext extends Obj = Obj
> = UseFormOptions<TFieldValues, TContext> & {
  apiError?: ApiError | Opt<ApiError>[];
};

type CustomSubmitHandler<TFieldValues extends FieldValues> = (
  data: UnpackNestedValue<TFieldValues>,
  event?: React.BaseSyntheticEvent
) => Observable<unknown> | void;

export default function useInputForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext extends Obj = Obj
>({
  apiError,
  ...opts
}: CustomUseFormOptions<
  TFieldValues,
  TContext
>): CustomUseFormMethods<TFieldValues> {
  const form = useForm<TFieldValues, TContext>({
    mode: 'onTouched',
    ...opts,
  });

  const submitSubscrpRef = useRef<Subscription>();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    control,
    errors,
    setError,
    handleSubmit,
    getValues,
    setValue,
  } = form;
  const { defaultValues } = opts;

  const customHandleSubmit: CustomUseFormMethods<TFieldValues>['handleSubmit'] =
    (onValid, onInvalid) => {
      const customOnValid: SubmitHandler<TFieldValues> = (
        ...args
      ) => {
        const rtn$ = onValid(...args);
        if (rtn$) {
          submitSubscrpRef.current = rtn$.subscribe(silenceError);
        }
      };
      return handleSubmit(customOnValid, onInvalid);
    };

  // NOTE: this provides the default values direct to the input components,
  // preventing the input box from having an initial momentary blank state.
  const inputProps = useMemo(
    () => ({ control, errors, defaults: defaultValues || {} }),
    [control, errors, defaultValues]
  );

  useEffect(() => {
    applyDefaultsIfEmpty({
      defaultValues,
      getValues,
      setValue,
    });
  }, [defaultValues, getValues, setValue]);

  useAddRequestErrorsToForm(
    setError,
    getValues,
    useMergeApiErrors(apiError)
  );

  useOnUnmount(() => submitSubscrpRef.current?.unsubscribe());

  return {
    ...form,
    inputProps,
    handleSubmit: customHandleSubmit,
  };
}
